/** Input type of the Study Builder notice rules (`page-rules.ts`) until they read the lifecycle state below. */
import type { PageActionDeclaration } from './page-declaration'
import type { FeedbackState } from './page-feedback'
import type { PageError, PageErrorRecord, SourceCheck } from './page-error-diagnostics'
export type { PageErrorName } from '../src/shared/page-error-contract'
export type { PageError } from './page-error-diagnostics'

export type PageState =
  | { readonly key: string; readonly status: 'loading' | 'restoring' | 'ready' }
  | { readonly key: string; readonly status: 'error'; readonly error: string }

/* ---------------------------------------------------------------------------------------------
 * Page lifecycle: named sources, named actions, named errors, refresh rules (#4264).
 * Pure and keyed by visit; `usePageLifecycle` is the only React bridge.
 * ------------------------------------------------------------------------------------------- */

/** The fixed list of moments every page shares. */
export type PageMoment =
  | 'opened'
  | 'data-arrived'
  | 'ready'
  | 'refreshed'
  | 'changed'
  | 'action-requested'
  | 'action-done'
  | 'error'

/**
 * An error is a name, an optional identifier code, and the merchant-safe `message` the Worker sent for it.
 * `message` is present only when the thrown value carried merchant copy (a `MerchantFacingError`, whose
 * wording the Worker chose from the error envelope); a raw network/parse failure has none, and the page
 * then shows the server words it already holds for that state (e.g. `readiness.onReadFailure`). The app
 * never composes `message` from a code of its own.
 */
const CODE_PREFIX = /^([A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+):/

// Browser fetch rejections: Chrome, Safari, Firefox wording. Only a TypeError with these messages is a network failure.
const FETCH_REJECTION = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/
// Transient HTTP statuses a retry can fix: request timeout, rate limit, bad gateway, unavailable, gateway timeout.
const TRANSIENT_STATUSES = [408, 429, 502, 503, 504]

/**
 * Names a thrown value. The code is `error.code`, else a leading `UPPER_SNAKE:` prefix of `error.message`
 * (what the plain study-builder errors carry; a `MerchantFacingError` sets `code` and keeps it out of its text). A value with no code gets no code here;
 * the lifecycle adds `source:<name>`. Only a `MerchantFacingError`'s merchant-safe text is kept (as `message`,
 * the Worker's wording for this error); any other thrown value's message text is never kept.
 */
export function pageErrorFrom(error: unknown): PageError {
  const record: PageErrorRecord = typeof error === 'object' && error !== null ? error : {}
  const message = typeof record.message === 'string' ? record.message : ''
  const code = typeof record.code === 'string' && record.code ? record.code : CODE_PREFIX.exec(message)?.[1]
  const status =
    typeof record.status === 'number' && Number.isInteger(record.status) && record.status >= 100 && record.status <= 599
      ? record.status
      : undefined
  const correlationId =
    typeof record.correlationId === 'string' && record.correlationId.trim()
      ? record.correlationId.trim().slice(0, 256)
      : undefined
  const diagnostics =
    status !== undefined || correlationId
      ? { ...(status !== undefined ? { status } : {}), ...(correlationId ? { correlationId } : {}) }
      : undefined
  // Only a MerchantFacingError's text is merchant-safe to keep; it is the Worker's wording for this error,
  // marked by its `isMerchantFacing` flag (detected structurally so this policy module stays pure). Any other
  // thrown value's message stays off the page — it may be developer prose.
  const merchantMessage = record.isMerchantFacing === true && message ? message : undefined
  const named = (name: PageError['name']): PageError => ({
    name,
    ...(code ? { code } : {}),
    ...(merchantMessage ? { message: merchantMessage } : {}),
    ...(diagnostics ? { diagnostics } : {}),
  })
  if (code?.endsWith('_NOT_FOUND')) return named('not-found')
  if (code?.endsWith('_VERSION_UNSUPPORTED')) return named('unsupported-version')
  if (code?.endsWith('_INVALID')) return named('invalid-data')
  if (code?.endsWith('_MISMATCH') || code?.endsWith('_CONFLICT')) return named('conflict')
  if (record.status === 404) return named('not-found')
  if (record.status === 409) return named('conflict')
  if (record.status === 401 || record.status === 403 || code?.endsWith('_FORBIDDEN') || code?.endsWith('_NOT_ALLOWED'))
    return named('not-allowed')
  // `network` is only a fetch rejection (TypeError with a browser wording) or a transient HTTP status; anything else is `unexpected`.
  const isFetchRejection = error instanceof TypeError && FETCH_REJECTION.test(message)
  if (isFetchRejection || (typeof record.status === 'number' && TRANSIENT_STATUSES.includes(record.status)))
    return named('network')
  return named('unexpected')
}

/** True for a thrown `AbortError`: the work was stopped on purpose and has no result. */
export function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** Query retry policy for page sources: only a network failure can succeed on retry; every other name, `unexpected` included, fails at once. */
export function retryNetworkOnly(failureCount: number, error: unknown): boolean {
  return failureCount < 3 && pageErrorFrom(error).name === 'network'
}

export type LifecycleState<Source extends string = string, Action extends string = string> =
  | {
      readonly key: string
      readonly status: 'loading'
      readonly required: readonly Source[]
      readonly arrived: readonly Source[]
    }
  | {
      readonly key: string
      readonly status: 'ready'
      readonly unsaved: boolean
      readonly running: Action | null
      /** True when `changed` fired while an action ran; a saving action then leaves `unsaved` true (R-k). */
      readonly changedWhileRunning: boolean
      readonly newerWaiting: readonly Source[]
      readonly refreshFailed: PageError | null
    }
  | { readonly key: string; readonly status: 'error'; readonly error: PageError }

export type LifecycleEvent<Source extends string = string, Action extends string = string> = {
  readonly key: string
} & (
  | { readonly type: 'data-arrived' | 'refreshed'; readonly source: Source }
  | { readonly type: 'refresh-failed'; readonly source: Source; readonly error: PageError }
  | { readonly type: 'changed' | 'newer-applied' | 'newer-dismissed' }
  | { readonly type: 'action-requested'; readonly action: Action }
  | { readonly type: 'action-done'; readonly action: Action; readonly saves: boolean }
  | { readonly type: 'action-failed'; readonly action: Action; readonly error: PageError }
  /** The action stopped without a result (its work was aborted); clears `running`, reports nothing. */
  | { readonly type: 'action-cancelled'; readonly action: Action }
  | { readonly type: 'failed'; readonly error: PageError }
)

function readyLifecycle<Source extends string, Action extends string>(
  key: string,
): Extract<LifecycleState<Source, Action>, { status: 'ready' }> {
  return {
    key,
    status: 'ready',
    unsaved: false,
    running: null,
    changedWhileRunning: false,
    newerWaiting: [],
    refreshFailed: null,
  }
}

export function initialLifecycle<Source extends string, Action extends string = string>(
  key: string,
  requiredSources: readonly Source[],
): LifecycleState<Source, Action> {
  if (!key.trim()) throw new Error('PAGE_SOURCE_REQUIRED')
  if (requiredSources.length === 0) return readyLifecycle(key)
  return { key, status: 'loading', required: requiredSources, arrived: [] }
}

function withSource<Source extends string>(sources: readonly Source[], source: Source): readonly Source[] {
  return sources.includes(source) ? sources : [...sources, source]
}

export function transitionLifecycle<Source extends string, Action extends string>(
  state: LifecycleState<Source, Action>,
  event: LifecycleEvent<Source, Action>,
): LifecycleState<Source, Action> {
  if (event.key !== state.key) return state
  if (event.type === 'failed') return { key: state.key, status: 'error', error: event.error }
  if (state.status === 'loading') return transitionLoading(state, event)
  if (state.status === 'error') return state
  return transitionReady(state, event)
}

function transitionLoading<Source extends string, Action extends string>(
  state: Extract<LifecycleState<Source, Action>, { status: 'loading' }>,
  event: LifecycleEvent<Source, Action>,
): LifecycleState<Source, Action> {
  if (event.type === 'refresh-failed') return { key: state.key, status: 'error', error: event.error }
  if (event.type !== 'data-arrived') return state
  const arrived = withSource(state.arrived, event.source)
  if (state.required.every((source) => arrived.includes(source))) return readyLifecycle(state.key)
  return arrived === state.arrived ? state : { ...state, arrived }
}

function transitionReady<Source extends string, Action extends string>(
  state: Extract<LifecycleState<Source, Action>, { status: 'ready' }>,
  event: LifecycleEvent<Source, Action>,
): LifecycleState<Source, Action> {
  switch (event.type) {
    case 'refreshed':
      if (!state.unsaved) return state.refreshFailed ? { ...state, refreshFailed: null } : state
      return { ...state, refreshFailed: null, newerWaiting: withSource(state.newerWaiting, event.source) }
    case 'refresh-failed':
      return { ...state, refreshFailed: event.error }
    case 'changed':
      if (state.running !== null)
        return state.changedWhileRunning ? state : { ...state, unsaved: true, changedWhileRunning: true }
      return state.unsaved ? state : { ...state, unsaved: true }
    case 'action-requested':
      return state.running === null ? { ...state, running: event.action } : state
    case 'action-done':
      if (state.running !== event.action) return state
      if (!event.saves) return { ...state, running: null, changedWhileRunning: false }
      // R-k: an edit made while the save ran is not saved, so it stays unsaved and keeps newer data waiting.
      return {
        ...state,
        running: null,
        unsaved: state.changedWhileRunning,
        newerWaiting: state.changedWhileRunning ? state.newerWaiting : [],
        changedWhileRunning: false,
      }
    case 'action-failed':
    case 'action-cancelled':
      return state.running === event.action ? { ...state, running: null, changedWhileRunning: false } : state
    case 'newer-applied':
      return { ...state, unsaved: false, changedWhileRunning: false, newerWaiting: [] }
    case 'newer-dismissed':
      return { ...state, newerWaiting: [] }
    default:
      return state
  }
}

/** What an action button shows (R: buttons read the page, never the editor). */
export type ActionButtonState = { readonly loading: boolean; readonly disabled: boolean }

/** Loading while this action runs; disabled while the page is not ready or a different action runs. */
export function actionButtonState<Action extends string>(
  state: LifecycleState<string, Action>,
  action: Action,
): ActionButtonState {
  if (state.status !== 'ready') return { loading: false, disabled: true }
  return { loading: state.running === action, disabled: state.running !== null && state.running !== action }
}

/* ---------------------------------------------------------------------------------------------
 * Page store: lifecycle state plus the checked data of each source (#4264, D5b).
 * `usePageLifecycle` keeps exactly one of these; every change goes through the functions below.
 * ------------------------------------------------------------------------------------------- */

/** The part of a TanStack `UseQueryResult` the lifecycle reads. */
export type SourceSignal = {
  /** Query identity within a stable page visit; cached pages have independent update clocks. */
  identity?: string
  data: unknown
  dataUpdatedAt: number
  isError: boolean
  errorUpdatedAt: number
  /** The query's error; named by `pageErrorFrom`. */
  error?: unknown
  /** Optional authoritative updates before React's batched query notification (e.g. action cancellation). */
  subscribe?: (accept: (signal: SourceSignal) => void) => () => void
}

type ByName<Source extends string, Value> = { readonly [N in Source]?: Value }

export type PageStore<Source extends string = string, Action extends string = string, Data = unknown> = {
  readonly feedback?: FeedbackState
  readonly state: LifecycleState<Source, Action>
  readonly required: readonly Source[]
  /** Last `dataUpdatedAt` / `errorUpdatedAt` handled per source, in this visit. */
  readonly handled: ByName<Source, { readonly data: number; readonly error: number; readonly identity?: string }>
  readonly applied: ByName<Source, Data>
  /** Newer data that refreshed while unsaved; applied only by `applyNewerData`. */
  readonly pending: ByName<Source, Data>
  /** Increments whenever `applied` changes; pages key their editor on `${visit}:${revision}`. */
  readonly revision: number
  /**
   * R-l: set when a saving action finished with nothing left unsaved. Cleared only by the refresh that
   * consumes it, `changed`, `refresh-failed`, `newer-applied`, `failed`, or a saving action that leaves edits unsaved.
   */
  readonly afterOwnSave?: true
  /** R-m: refreshed data that arrived while an action ran; processed by the normal rules when the action ends. */
  readonly held?: ByName<Source, Data>
  /**
   * Set when the merchant pressed "Load saved version" to recover a revision conflict (a save/Submit from a stale
   * copy). The next refreshed source is applied straight away, replacing unsaved edits, through the same path the
   * newer-version notice uses (`applyNewerData`) — one press, no second chooser. Cleared by that apply or by a failed
   * refresh (so it never poisons a later focus refetch). Unset for a focus refetch, which keeps the newer-version rule.
   */
  readonly loadingSaved?: true
}

/** What the helper should report to analytics after a source update; fixed identifiers only. */
export type SourceReport<Source extends string = string> =
  | { readonly type: 'arrived' | 'refreshed'; readonly source: Source }
  | { readonly type: 'failed'; readonly source: Source; readonly moment: PageMoment; readonly error: PageError }

export function initialPageStore<Source extends string, Action extends string, Data = unknown>(
  key: string,
  requiredSources: readonly Source[],
): PageStore<Source, Action, Data> {
  return {
    state: initialLifecycle<Source, Action>(key, requiredSources),
    required: requiredSources,
    handled: {},
    applied: {},
    pending: {},
    revision: 0,
  }
}

/** Run a lifecycle event on the store. Newer data that is no longer waiting is dropped. */
export function reduceStoreEvent<Source extends string, Action extends string, Data>(
  store: PageStore<Source, Action, Data>,
  event: LifecycleEvent<Source, Action>,
): PageStore<Source, Action, Data> {
  const state = transitionLifecycle(store.state, event)
  if (state === store.state) return store
  const waiting = state.status === 'ready' && state.newerWaiting.length > 0
  const ownSave = event.type === 'action-done' && event.saves && state.status === 'ready' && !state.unsaved
  const clearsMark =
    ['changed', 'refresh-failed', 'newer-applied', 'failed'].includes(event.type) ||
    (event.type === 'action-done' && event.saves)
  const mark = ownSave || (!clearsMark && store.afterOwnSave === true) ? (true as const) : undefined
  // A failed refresh never delivers the saved version, so drop the recovery flag rather than let it apply a later
  // focus refetch. The successful apply clears it in `applyRefreshed`.
  const keepLoadingSaved = store.loadingSaved && event.type !== 'refresh-failed' && event.type !== 'failed'
  const next = {
    ...store,
    state,
    pending: waiting ? store.pending : {},
    afterOwnSave: mark,
    loadingSaved: keepLoadingSaved ? (true as const) : undefined,
  }
  const wasRunning = store.state.status === 'ready' && store.state.running !== null
  const isRunning = state.status === 'ready' && state.running !== null
  if (!wasRunning || isRunning) return next
  // R-m: the action ended, so data held during it is processed now by the normal rules.
  return state.status === 'ready' ? releaseHeld(next) : { ...next, held: undefined }
}

function releaseHeld<Source extends string, Action extends string, Data>(
  store: PageStore<Source, Action, Data>,
): PageStore<Source, Action, Data> {
  const held: ByName<Source, Data> = store.held ?? {}
  let next: PageStore<Source, Action, Data> = { ...store, held: undefined }
  for (const source of Object.keys(held) as Source[]) next = applyRefreshed(next, source, held[source] as Data)
  return next
}

/** Structural equality for checked (JSON-shaped) source data. Pure; never throws. Used to tell a real
 *  newer save from a focus refetch that returned the same saved draft this page already applied. */
function sourceDataEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, index) => sourceDataEquals(item, b[index]))
  }
  const left = a as Record<string, unknown>
  const right = b as Record<string, unknown>
  const leftKeys = Object.keys(left)
  if (leftKeys.length !== Object.keys(right).length) return false
  return leftKeys.every((key) => Object.hasOwn(right, key) && sourceDataEquals(left[key], right[key]))
}

/** Checked refreshed data meets the page: unsaved edits make it pending, otherwise it is applied (quietly after the page's own save, R-l). */
function applyRefreshed<Source extends string, Action extends string, Data>(
  store: PageStore<Source, Action, Data>,
  source: Source,
  data: Data,
): PageStore<Source, Action, Data> {
  const unsaved = store.state.status === 'ready' && store.state.unsaved
  const savedByPage = store.afterOwnSave === true
  // The merchant pressed "Load saved version" to recover a revision conflict: apply this refreshed draft straight
  // away, replacing unsaved edits, through the newer-version path (`applyNewerData`). One press, no second chooser.
  if (store.loadingSaved) {
    const cleared = { ...store, loadingSaved: undefined }
    if (sourceDataEquals(store.applied[source], data)) return applyNewerData(cleared)
    const next = reduceStoreEvent(cleared, { type: 'refreshed', key: store.state.key, source })
    return applyNewerData({ ...next, pending: { ...next.pending, [source]: data } })
  }
  // A focus refetch that returns the same saved draft this page already applied is not a newer version:
  // only data that truly differs from the applied baseline waits. The baseline holds the saved draft;
  // local unsaved edits live in the editor, not here. Keep the edits intact and raise no notice, but clear
  // any earlier refresh failure, as a successful refresh does.
  if (unsaved && sourceDataEquals(store.applied[source], data)) {
    if (store.state.status === 'ready' && store.state.refreshFailed)
      return { ...store, state: { ...store.state, refreshFailed: null } }
    return store
  }
  const next = reduceStoreEvent(store, { type: 'refreshed', key: store.state.key, source })
  if (unsaved) return { ...next, pending: { ...next.pending, [source]: data } }
  return {
    ...next,
    applied: { ...next.applied, [source]: data },
    revision: savedByPage ? next.revision : next.revision + 1,
    afterOwnSave: undefined,
  }
}

/** Apply one query result to the store: arrival, refresh or failure, each handled once per update time. */
export function reduceSourceUpdate<Source extends string, Action extends string, Data>(
  store: PageStore<Source, Action, Data>,
  source: Source,
  signal: SourceSignal,
  check: SourceCheck,
  isReady?: (data: Data, source: Source) => boolean,
): { store: PageStore<Source, Action, Data>; reports: SourceReport<Source>[] } {
  const key = store.state.key
  const previous = store.handled[source] ?? { data: 0, error: 0 }
  const seen = signal.identity === previous.identity ? previous : { data: 0, error: 0, identity: signal.identity }
  const reports: SourceReport<Source>[] = []
  let next = store
  const failure = (moment: PageMoment, error: PageError, event: 'failed' | 'refresh-failed') => {
    next = reduceStoreEvent(next, { type: event, key, source, error } as LifecycleEvent<Source, Action>)
    reports.push({ type: 'failed', source, moment, error })
  }
  if (signal.data !== undefined && signal.dataUpdatedAt > seen.data) {
    next = {
      ...next,
      handled: {
        ...next.handled,
        [source]: { ...seen, data: signal.dataUpdatedAt },
      },
    }
    const first = !Object.hasOwn(next.applied, source)
    const parsed = check(signal.data)
    if (!parsed.success) {
      const validationPath = parsed.error?.issues?.[0]?.path.map(String).join('.') || '(root)'
      const error: PageError = {
        name: 'invalid-data',
        code: `source:${source}`,
        diagnostics: { validationPath },
      }
      failure(first ? 'data-arrived' : 'refreshed', error, first ? 'failed' : 'refresh-failed')
    } else {
      const data = parsed.data as Data
      if (isReady && !isReady(data, source)) {
        // Revocation outranks unsaved edits and running actions: forbidden/stale data is not held for later.
        const applied = { ...next.applied, [source]: data }
        const previouslyArrived = next.state.status === 'loading' ? next.state.arrived : Object.keys(next.applied)
        const arrived = previouslyArrived.filter((name) => name !== source) as Source[]
        next = {
          ...next,
          state: next.required.includes(source)
            ? { key, status: 'loading', required: next.required, arrived }
            : next.state,
          applied,
          pending: {},
          held: undefined,
          afterOwnSave: undefined,
          revision: next.revision + 1,
        }
      } else if (
        first ||
        (isReady && next.state.status === 'error') ||
        (next.state.status === 'loading' && !next.state.arrived.includes(source))
      ) {
        // A gated source can recover after a cached preparing response and a failed refresh.
        // Rebuild from every checked source; another still-preparing source must not become ready.
        if (isReady && next.state.status === 'error') {
          const arrived = (Object.keys(next.applied) as Source[]).filter((name) => isReady(next.applied[name]!, name))
          next = { ...next, state: { key, status: 'loading', required: next.required, arrived } }
        }
        next = reduceStoreEvent(next, { type: 'data-arrived', key, source })
        next = { ...next, applied: { ...next.applied, [source]: data }, revision: next.revision + 1 }
      } else if (next.state.status === 'ready' && next.state.running !== null) {
        // R-m: no remount while an action runs; the data waits for the action to end.
        next = { ...next, held: { ...next.held, [source]: data } }
      } else next = applyRefreshed(next, source, data)
      reports.push({ type: first ? 'arrived' : 'refreshed', source })
    }
  }
  if (signal.isError && signal.errorUpdatedAt > seen.error) {
    const first = !Object.hasOwn(next.applied, source)
    next = {
      ...next,
      handled: {
        ...next.handled,
        [source]: {
          ...(next.handled[source] ?? seen),
          error: signal.errorUpdatedAt,
        },
      },
    }
    const named = pageErrorFrom(signal.error)
    const error: PageError = { ...named, code: named.code ?? `source:${source}` }
    failure(first ? 'error' : 'refreshed', error, first ? 'failed' : 'refresh-failed')
  }
  return { store: next, reports }
}

/** "Load newer version": waiting data becomes applied and the page is clean again. */
export function applyNewerData<Source extends string, Action extends string, Data>(
  store: PageStore<Source, Action, Data>,
): PageStore<Source, Action, Data> {
  const hasPending = Object.keys(store.pending).length > 0
  const state = transitionLifecycle(store.state, { type: 'newer-applied', key: store.state.key })
  if (!hasPending) return state === store.state ? store : { ...store, state, afterOwnSave: undefined }
  return {
    ...store,
    state,
    afterOwnSave: undefined,
    applied: { ...store.applied, ...store.pending },
    pending: {},
    revision: store.revision + 1,
  }
}

/** Interaction permissions read the running action's declaration, never its name. */
export function pageInteractionState<Action extends string>(
  state: LifecycleState<string, Action>,
  actions: Record<Action, PageActionDeclaration>,
) {
  if (state.status !== 'ready') return { textDisabled: true, structureDisabled: true }
  if (state.running === null) return { textDisabled: false, structureDisabled: false }
  return { textDisabled: actions[state.running].interaction.textEditing === 'block', structureDisabled: true }
}
